import { AggregatedArticle } from "./useAggregatedArticles";
import { useMemoryFilteredData } from "@coworker/apprestructured/src/shared/filters/utils/apply/useMemoryFilteredData";
import { useHfbMultiSelectArticleFilter } from "../filters/useHfbMultiSelectArticleFilter";
import { useAlertSelectArticleFilter } from "../filters/useAlertSelectArticleFilter";
import { useEdsDatesSelectArticleFilter } from "../filters/useEdsDatesSelectArticleFilter";
import { useTasksSelectArticleFilter } from "../filters/useTasksSelectArticleFilter";
import { useGroupedArticlesSelectArticleFilter } from "../filters/useGroupedArticlesSelectArticleFilter";
import { useSortSelectArticleFilter } from "../filters/useSortSelectArticleFilter";
import { useProductAttributesMultiSelectArticleFilter } from "../filters/useProductAttributesMultiSelectArticleFilter";
import preferences from "@coworker/enums/profilePreferences";
import { useUserPreferenceStoreFilterValues } from "@coworker/apprestructured/src/shared/filters/utils/store/useUserPreferenceStoreFilterValues";
import { useEdsDates } from "./useEdsDates";
import {
  FilterValue,
  InputFilter,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { useHfbDepartments } from "./useHfbDepartments";
import { ApplyFilter } from "@coworker/apprestructured/src/shared/filters/types/apply";
import { Media } from "../types/media";

function useBaseMediaDataFilters(
  articles: AggregatedArticle[] | undefined,
  values: FilterValue[],
  media?: Media
) {
  const hfbDepartments = useHfbDepartments(articles ?? []);
  const edsDates = useEdsDates(articles ?? []);

  return {
    hfbFilter: useHfbMultiSelectArticleFilter(hfbDepartments),
    alertFilter: useAlertSelectArticleFilter(),
    edsFilter: useEdsDatesSelectArticleFilter(values, edsDates),
    tasksFilter: useTasksSelectArticleFilter(),
    groupedFilter: useGroupedArticlesSelectArticleFilter(),
    productAttributesFilter:
      useProductAttributesMultiSelectArticleFilter(media),
    sortFilter: useSortSelectArticleFilter(values),
  };
}

function useBaseMediaArticlesFilters(
  articles: AggregatedArticle[] | undefined,
  media: Media | undefined,
  buildDataFilters: (
    allFilters: ReturnType<typeof useBaseMediaDataFilters>
  ) => ApplyFilter<InputFilter, AggregatedArticle>[]
) {
  const { filterValues, setFilterValues } = useUserPreferenceStoreFilterValues(
    preferences.MEDIA_FILTERS_ARTICLES
  );

  const allFiltersForStore = useBaseMediaDataFilters(
    articles,
    filterValues,
    media
  );
  const dataFilters = buildDataFilters(allFiltersForStore);

  const useFiltersFactory = (values: FilterValue[]) => {
    const allFiltersForFactory = useBaseMediaDataFilters(
      articles,
      values,
      media
    );
    const factoryDataFilters = buildDataFilters(allFiltersForFactory);
    return factoryDataFilters.map(({ filter }) => filter);
  };

  function useFilteredArticleData(articles: AggregatedArticle[] | undefined) {
    return useMemoryFilteredData<AggregatedArticle>(
      articles,
      dataFilters,
      filterValues
    );
  }

  return {
    useFiltersFactory,
    filterValues,
    setFilterValues,
    useFilteredArticleData,
  };
}

// This code will probably go into AggregatedArticleListing when old filter solution is removed
export function useMediaArticlesFilters(
  articles: AggregatedArticle[] | undefined,
  media?: Media,
  enableProductAttributesFilter = false
) {
  return useBaseMediaArticlesFilters(articles, media, (filters) => [
    filters.hfbFilter,
    filters.alertFilter,
    filters.edsFilter,
    filters.tasksFilter,
    filters.groupedFilter,
    ...(enableProductAttributesFilter ? [filters.productAttributesFilter] : []),
    filters.sortFilter,
  ]);
}

export function useGroupMediaArticlesFilters(
  articles: AggregatedArticle[] | undefined
) {
  return useBaseMediaArticlesFilters(articles, undefined, (filters) => [
    filters.hfbFilter,
    filters.alertFilter,
    filters.edsFilter,
    filters.tasksFilter,
    filters.sortFilter,
  ]);
}
